/**
 * ChatInput Component
 *
 * Auto-resizing textarea for sending chat messages.
 * Enter to send, Shift+Enter for newline.
 */

import { Component, createSignal, Show } from "solid-js";
import { Button } from "../ui/Button";

export interface ChatInputProps {
  /** Callback when user sends a message */
  onSend: (message: string) => void;
  /** Whether input is disabled (e.g., AI responding) */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Callback to cancel an in-flight AI request. When provided while disabled, shows a stop button. */
  onCancel?: () => void;
  /** Called when Up arrow is pressed in an empty input */
  onUpArrowEmpty?: () => void;
  /** Expose the textarea element to the parent */
  onRef?: (el: HTMLTextAreaElement) => void;
  /** Show keyboard shortcuts help modal */
  onShowShortcuts?: () => void;
}

export const ChatInput: Component<ChatInputProps> = (props) => {
  const [value, setValue] = createSignal("");
  let textareaRef: HTMLTextAreaElement | undefined;

  const handleInput = (e: InputEvent) => {
    const target = e.currentTarget as HTMLTextAreaElement;
    target.style.height = "auto";
    target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
    setValue(target.value);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    const metaOrCtrl = e.metaKey || e.ctrlKey;

    if (e.key === "Enter" && metaOrCtrl) {
      e.preventDefault();
      handleSend();
      return;
    }

    if (e.key === "Enter" && !e.shiftKey && !metaOrCtrl) {
      e.preventDefault();
      handleSend();
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setValue("");
      if (textareaRef) textareaRef.style.height = "auto";
      return;
    }

    if (e.key === "ArrowUp" && value() === "") {
      e.preventDefault();
      props.onUpArrowEmpty?.();
      return;
    }
  };

  const handleSend = () => {
    const trimmed = value().trim();
    if (!trimmed || props.disabled) return;

    props.onSend(trimmed);
    setValue("");

    if (textareaRef) {
      textareaRef.style.height = "auto";
    }
  };

  return (
    <div class="border-t border-line bg-surface p-4">
      <div class="flex gap-3 items-end">
        <textarea
          ref={(el) => {
            textareaRef = el;
            props.onRef?.(el);
          }}
          value={value()}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={props.placeholder ?? "Type a message..."}
          disabled={props.disabled}
          rows={1}
          class={
            "flex-1 resize-none rounded border border-line px-4 py-3 text-sm " +
            "focus-ring " +
            "disabled:bg-sunken disabled:text-ink-3 disabled:cursor-not-allowed " +
            "placeholder:text-ink-3"
          }
          style={{ "min-height": "44px", "max-height": "200px" }}
        />
        <Show
          when={props.disabled && props.onCancel}
          fallback={
            <Button
              variant="primary"
              onClick={handleSend}
              disabled={props.disabled || !value().trim()}
              class="shrink-0"
            >
              <SendIcon />
            </Button>
          }
        >
          <Button
            variant="danger"
            onClick={() => props.onCancel?.()}
            class="shrink-0"
            aria-label="Stop generating"
          >
            <StopIcon />
          </Button>
        </Show>
      </div>
      <div class="flex items-center justify-between text-xs text-ink-3 mt-2">
        <span>
          Enter to send · Shift+Enter for new line · /clear to reset ·{" "}
          <kbd class="font-mono">↑</kbd> to edit last message
        </span>
        <Show when={props.onShowShortcuts}>
          <button
            type="button"
            class="text-ink-3 hover:text-ink transition-colors"
            onClick={() => props.onShowShortcuts?.()}
            title="Keyboard shortcuts"
            aria-label="Show keyboard shortcuts"
          >
            <span class="font-mono text-xs border border-line rounded px-1">
              ?
            </span>
          </button>
        </Show>
      </div>
    </div>
  );
};

const SendIcon: Component = () => (
  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
  </svg>
);

const StopIcon: Component = () => (
  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

export default ChatInput;
